import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  setInseratStatus,
  type Inserat,
  type InseratStatus,
  type InserateListResponse,
} from "@/api/inserate";

const OPTIONS: { value: InseratStatus; label: string }[] = [
  { value: "neu", label: "Neu" },
  { value: "shortlist", label: "Shortlist" },
  { value: "in-analyse", label: "In Analyse" },
  { value: "gekauft", label: "Gekauft" },
  { value: "dismissed", label: "Verworfen" },
  { value: "expired", label: "Abgelaufen" },
];

const LABEL: Record<InseratStatus, string> = Object.fromEntries(
  OPTIONS.map((o) => [o.value, o.label]),
) as Record<InseratStatus, string>;

interface OptimisticContext {
  snapshots: [readonly unknown[], InserateListResponse | undefined][];
}

/** Per-row dropdown that mutates status with optimistic update + toast. */
export function StatusCell({ inserat }: { inserat: Inserat }) {
  const qc = useQueryClient();

  const mutation = useMutation<unknown, Error, InseratStatus, OptimisticContext>({
    mutationFn: (status) => setInseratStatus(inserat.key, status),

    onMutate: async (status) => {
      await qc.cancelQueries({ queryKey: ["inserate"] });
      const snapshots = qc.getQueriesData<InserateListResponse>({
        queryKey: ["inserate"],
      });
      qc.setQueriesData<InserateListResponse | undefined>(
        { queryKey: ["inserate"] },
        (old) =>
          old
            ? {
                ...old,
                inserate: old.inserate.map((i) =>
                  i.key === inserat.key ? { ...i, status } : i,
                ),
              }
            : old,
      );
      return { snapshots };
    },

    onError: (err, _status, ctx) => {
      // Rollback: restore every snapshot we captured
      ctx?.snapshots.forEach(([key, value]) => qc.setQueryData(key, value));
      toast.error("Status-Update fehlgeschlagen", {
        description: err instanceof Error ? err.message : undefined,
      });
    },

    onSuccess: (_data, status) => {
      toast.success(`Status: ${LABEL[status]}`);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["inserate"] });
    },
  });

  return (
    <select
      value={inserat.status}
      disabled={mutation.isPending}
      onChange={(e) => mutation.mutate(e.target.value as InseratStatus)}
      data-testid={`status-select-${inserat.key}`}
      aria-label={`Status für ${inserat.title ?? inserat.key}`}
      className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
